import { useCallback, useMemo } from "react";
import { trackExperimentExposure } from "../services/gtm";
import type {
  ExperimentAssignment,
  ExperimentAssignmentSource,
  PawsExperiments,
} from "./types";

// Beacon base URL — MUST match the paws-admin deployment host (and the const
// in snippets/paws-experiments.liquid).
const BEACON_URL = "https://admin.pawsonplates.co.uk/api/public/experiments/beacon";

// Read once at module load. The bootstrap snippet runs synchronously in
// <head>, so the store exists (or never will) before any React code executes.
// Reading once keeps every render of every hook consumer stable.
const store: Readonly<PawsExperiments> | null =
  typeof window !== "undefined" && window.__PAWS_EXPERIMENTS__
    ? window.__PAWS_EXPERIMENTS__
    : null;

export interface UseExperimentResult {
  /** The experiment key that was asked for. */
  key: string;
  /** Experiment version the assignment was made under; null when absent. */
  version: number | null;
  /**
   * Assigned variant key, or null when the experiment is not running / the
   * bootstrap did not run. Callers MUST render the control experience when
   * this is null (fail open).
   */
  variant: string | null;
  /** How the variant was chosen; null when absent. */
  source: ExperimentAssignmentSource | null;
  /**
   * Call when the experiment surface actually became visible to the user.
   * Deduped per session; pushes `experiment_exposure` to GTM and beacons
   * kind:"exposed" to paws-admin. No-op for absent or QA assignments.
   */
  trackExposure: () => void;
}

export function useExperiment(key: string): UseExperimentResult {
  const assignment: ExperimentAssignment | null =
    store?.assignments?.[key] ?? null;

  const trackExposure = useCallback(() => {
    if (!store || !assignment) return;
    // QA overrides never emit analytics or beacons.
    if (assignment.src === "qa_override") return;

    const dedupeKey = `paws_exp_exposed:${key}`;
    try {
      if (window.sessionStorage.getItem(dedupeKey)) return;
      window.sessionStorage.setItem(dedupeKey, "1");
    } catch (e) {
      // sessionStorage unavailable — still fire (at worst once per page load).
    }

    trackExperimentExposure({
      experimentKey: key,
      version: assignment.v,
      variant: assignment.variant,
      source: assignment.src,
    });

    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      try {
        navigator.sendBeacon(
          BEACON_URL,
          JSON.stringify({
            k: key,
            v: assignment.v,
            a: assignment.variant,
            sid: store.visitorId,
            kind: "exposed",
          })
        );
      } catch (e) {
        // fire and forget
      }
    }
  }, [key, assignment]);

  return useMemo(
    () => ({
      key,
      version: assignment?.v ?? null,
      variant: assignment?.variant ?? null,
      source: assignment?.src ?? null,
      trackExposure,
    }),
    [key, assignment, trackExposure]
  );
}
